import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';

export interface AcaoDoMenu {
  rotulo: string;
  onSelect: () => void;
  perigo?: boolean;
}

/**
 * Menu de acoes da linha, como o botao de tres pontos do OnPoint.
 *
 * O menu fecha pela tecla de escape e pelo clique fora, e cada item e um botao com
 * papel de item de menu: quem usa teclado ou leitor de tela chega a ele como chega
 * ao resto da tela.
 */
export function MenuDeAcoes({
  titulo,
  acoes,
}: {
  titulo: string;
  acoes: AcaoDoMenu[];
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const idDoMenu = useId();

  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: MouseEvent) => {
      if (!raiz.current?.contains(evento.target as Node)) setAberto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  if (acoes.length === 0) return null;

  return (
    <div className="relative" ref={raiz} onClick={(evento) => evento.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoMenu : undefined}
        aria-label={titulo}
        onClick={() => setAberto((atual) => !atual)}
        className="flex h-8 items-center gap-1 rounded-md border border-line px-2.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
      >
        Acoes
        <span aria-hidden="true">...</span>
      </button>
      {aberto ? (
        <ul
          id={idDoMenu}
          role="menu"
          aria-label={titulo}
          className="surface-card absolute left-0 z-30 mt-1 w-56 p-1 shadow-(--shadow-lg)"
        >
          {acoes.map((acao) => (
            <li key={acao.rotulo} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false);
                  acao.onSelect();
                }}
                className={cn(
                  'w-full rounded-md px-3 py-2 text-left text-sm hover:bg-ink-50',
                  acao.perigo ? 'text-red-700' : 'text-ink-800',
                )}
              >
                {acao.rotulo}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
